import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE from '../api';
import DeclineCoachMatches, { DeclineMatchThankYouScreen } from '../components/DeclineCoachMatches';
import { filterCoachSlotLabelsFromToday } from '../utils/coachSlotDates';
import {
  timezoneAbbrevFromLabel,
  formatPrimaryWeeklyLine,
} from '../utils/scheduleDisplay';
import {
  pickSlotForWeek,
  slotPatternKey,
  typicalTimeFromSlot,
} from '../utils/lessonPlan';
import { parseSlotsFromCalendlyRecords } from '../utils/matchCalendlySlots';
import { DateTime } from 'luxon';
import { Pencil, Check, Clock } from 'lucide-react';
import * as confettiModule from 'canvas-confetti';
const confetti = confettiModule.default || confettiModule;

function loadDraftsFromStorage(requestId) {
  if (!requestId || typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(`coachSelectionDrafts:${requestId}`);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

const BRAND = '#c1009d';
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIME_SLOTS = [];
for (let h = 6; h <= 21; h++) {
  const start = `${h}:00`;
  const end = `${h + 1}:00`;
  const fmt = (hr) => {
    const period = hr >= 12 ? 'PM' : 'AM';
    const h12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
    return `${h12}:00 ${period}`;
  };
  TIME_SLOTS.push({ start, end, label: `${fmt(h)} – ${fmt(h + 1)}` });
}

const TZ_MAP = {
  'Eastern Time (ET)': 'America/New_York',
  'Pacific Time (PT)': 'America/Los_Angeles',
  'Mountain Time (MT)': 'America/Denver',
  'Central Time (CT)': 'America/Chicago',
  '北京时间': 'Asia/Shanghai',
  'Central European Time (CET)': 'Europe/Paris',
};

function getIanaTz(label) {
  if (typeof label === 'string' && label.includes('/')) return label;
  return TZ_MAP[label] || 'America/New_York';
}

function toEmbedUrl(url) {
  if (!url) return '';
  const trimmed = String(url).trim();
  if (trimmed.includes('drive.google.com')) return trimmed.replace('/view', '/preview');
  return trimmed;
}

function slotHasDate(label) {
  if (!label || typeof label !== 'string') return false;
  return /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\b/i.test(label) && /\s+at\s+\d{1,2}:\d{2}/i.test(label);
}

function normalizeSlotLabel(label) {
  if (!label || typeof label !== 'string') return '';
  // Strip trailing timezone suffix (e.g. "PDT", "EST", "CET") but NEVER the
  // "AM"/"PM" meridiem — those are also 2 uppercase letters and would otherwise
  // silently break parseSlotDT, which requires AM/PM to match.
  return String(label)
    .replace(/\s*\(.*?\)\s*$/, '')
    .replace(/\s+(?!AM\s*$|PM\s*$)[A-Z]{2,5}\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Cheap day-of-week extraction from a card label. Regex-only (no date parsing), so this
 * still works if the label is malformed/locale-weird. Returns Luxon weekday numbering
 * (1=Mon … 7=Sun) or null if no day prefix found.
 */
function extractWeekdayFromLabel(label) {
  if (!label || typeof label !== 'string') return null;
  const m = String(label).trim().match(/\b(Sun|Mon|Tue|Wed|Thu|Fri|Sat)/i);
  if (!m) return null;
  const key = m[1].toLowerCase();
  const map = { sun: 7, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
  return map[key] || null;
}

// Parse slot label ("Mon, Jan 12 at 3:00 PM …") into luxon DateTime in student's zone (not hardcoded ET).
function parseSlotDT(label, year, zone) {
  if (!label || typeof label !== 'string') return null;
  const clean = normalizeSlotLabel(label);
  const m = clean.match(/(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2})\s+at\s+(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!m) return null;
  const [, mo, dy, hr, mn, per] = m;
  const z = zone || 'America/New_York';
  const dt = DateTime.fromFormat(`${mo} ${dy} ${year} ${hr}:${mn} ${per}`, "MMM d yyyy h:mm a", { zone: z });
  return dt.isValid ? dt : null;
}

// Convert a parsed luxon DT to display fields in a given IANA timezone
function formatDTinTz(dt, ianaTz) {
  const c = dt.setZone(ianaTz);
  return {
    dayName: c.toFormat('EEEE'),
    dayShort: c.toFormat('EEE'),
    dateStr: c.toFormat('MMM d'),
    timeStr: c.toFormat('h:mm a'),
    display: c.toFormat("EEE, MMM d 'at' h:mm a"),
    date: c.toJSDate(),
    dayOfWeek: c.weekday % 7,
    hours24: c.hour,
    minutes: c.minute,
  };
}

// Convert a slot label string from ET to the student's timezone
function convertSlotLabel(label, studentTzLabel, year) {
  if (!label || typeof label !== 'string') return label;
  const ianaTz = getIanaTz(studentTzLabel);
  const dt = parseSlotDT(label, year, ianaTz);
  if (!dt) return label;
  return dt.setZone(ianaTz).toFormat("EEE, MMM d 'at' h:mm a");
}

/** Same slot pipeline as the coach cards (and must match the detail page’s first recommended time). */
function getCoachCardDisplayLabels(match, termStartDate, studentTimezone, year) {
  const times = [
    ...(match.exact_slots || []),
    ...(match.nearby_slots || []),
    ...(match.all_coach_slots || []).slice(0, 3),
  ];
  const seen = new Set();
  const deduped = times.filter((t) => {
    if (!slotHasDate(t)) return false;
    const key = normalizeSlotLabel(t);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const filtered = filterCoachSlotLabelsFromToday(deduped, termStartDate || '').slice(0, 3);
  return filtered.map((t) => convertSlotLabel(t, studentTimezone, year));
}

function formatHourLabel(t) {
  const [h] = String(t || '0:00').split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}${period}`;
}

/** Preserve parent form order: first listed day wins when multiple slots tie on tier. */
function buildPreferredDayRankMap(availabilitySlots) {
  if (!Array.isArray(availabilitySlots) || availabilitySlots.length === 0) return null;
  const m = new Map();
  for (const w of availabilitySlots) {
    const day = typeof w?.day === 'string' ? w.day.trim() : '';
    if (day && !m.has(day)) m.set(day, m.size);
  }
  return m.size > 0 ? m : null;
}

function preferredDaySortKey(slot, prefMap) {
  if (!prefMap || !slot) return 999;
  const name = slot.dayName;
  if (typeof name === 'string' && prefMap.has(name)) return prefMap.get(name);
  return 999;
}

/** Monday-based week key in the student's timezone (not the browser's local TZ). */
function getWeekMondayKeyInZone(slot, studentTimezone) {
  const zone = getIanaTz(studentTimezone);
  const dt = dateTimeInStudentZoneFromSlot(slot, zone);
  if (!dt || !dt.isValid) return '';
  const daysFromMonday = dt.weekday - 1; // Luxon: Monday = 1
  return dt.minus({ days: daysFromMonday }).startOf('day').toISODate();
}

function isLiveLuxonDT(v) {
  return !!(
    v &&
    typeof v.setZone === 'function' &&
    typeof v.toFormat === 'function' &&
    typeof v.isValid === 'boolean' &&
    v.isValid
  );
}

/** Normalize Calendly/ISO strings so draft rehydration matches the same instant. */
function canonicalUtcKey(u) {
  if (u == null || u === '') return '';
  const d = DateTime.fromISO(String(u).trim(), { setZone: true });
  return d.isValid ? d.toUTC().toISO() : String(u).trim();
}

function dateTimeInStudentZoneFromSlot(selected, zone) {
  if (!selected) return null;
  if (selected.utc) {
    const dt = DateTime.fromISO(String(selected.utc).trim(), { setZone: true }).setZone(zone);
    return dt.isValid ? dt : null;
  }
  if (isLiveLuxonDT(selected.luxonDT)) {
    const dt = selected.luxonDT.setZone(zone);
    return dt.isValid ? dt : null;
  }
  if (selected.date != null) {
    const js = selected.date instanceof Date ? selected.date : new Date(selected.date);
    if (!Number.isNaN(js.getTime())) {
      const dt = DateTime.fromMillis(js.getTime()).setZone(zone);
      return dt.isValid ? dt : null;
    }
  }
  return null;
}

/** Prefer absolute instant (utc / JS epoch) so weekday + calendar date always agree. */
function formatSelectedSlotLine(selected, studentTimezone, tzAbbr) {
  if (!selected) return '';
  const zone = getIanaTz(studentTimezone);
  const dt = dateTimeInStudentZoneFromSlot(selected, zone);
  if (dt) {
    return `${dt.toFormat('EEE, MMM d, yyyy')} at ${dt.toFormat('h:mm a')} ${tzAbbr}`;
  }
  return `${selected.dayShort}, ${selected.dateStr} at ${selected.timeStr} ${tzAbbr}`;
}

/** Picker row: same instant logic as main row (no tz suffix). */
function formatSlotPickerLabel(alt, studentTimezone) {
  const zone = getIanaTz(studentTimezone);
  const dt = dateTimeInStudentZoneFromSlot(alt, zone);
  if (dt) {
    return `${dt.toFormat('EEE')} ${dt.toFormat('MMM d, yyyy')} @ ${dt.toFormat('h:mm a')}`;
  }
  return `${alt.dayShort} ${alt.dateStr} @ ${alt.timeStr}`;
}

function typicalTimeFromSelectedSlot(selected, studentTimezone) {
  if (!selected) return null;
  const zone = getIanaTz(studentTimezone);
  const dt = dateTimeInStudentZoneFromSlot(selected, zone);
  if (dt) {
    return { dayOfWeek: dt.weekday % 7, hours24: dt.hour, minutes: dt.minute };
  }
  return typicalTimeFromSlot(selected);
}

/**
 * localStorage JSON destroys Luxon DateTime objects. Re-bind each draft selection to live slot rows by `utc` / `original`.
 */
function rehydrateDraftLessons(draftLessons, planLessons) {
  if (!Array.isArray(draftLessons) || !Array.isArray(planLessons)) return draftLessons;

  const keyToSlot = new Map();
  for (const pl of planLessons) {
    for (const alt of pl.alternatives || []) {
      const u = alt?.utc && String(alt.utc).trim();
      if (u) {
        keyToSlot.set(`utc:${canonicalUtcKey(u)}`, alt);
        keyToSlot.set(`utc:${u}`, alt);
      }
      const o = alt?.original && String(alt.original).trim();
      if (o) keyToSlot.set(`orig:${o}`, alt);
    }
  }

  return draftLessons.map((dl, i) => {
    const pl = planLessons[i];
    const alternatives = pl?.alternatives?.length ? pl.alternatives : (dl.alternatives || []);
    const sel = dl.selected;
    let live = null;
    if (sel?.utc) {
      const raw = String(sel.utc).trim();
      live = keyToSlot.get(`utc:${canonicalUtcKey(raw)}`) || keyToSlot.get(`utc:${raw}`) || null;
    }
    if (!live && sel?.original) live = keyToSlot.get(`orig:${String(sel.original).trim()}`) || null;
    const selected = live || sel;
    return {
      ...dl,
      alternatives,
      selected,
      initialSelectedDisplay: selected?.display != null ? selected.display : dl.initialSelectedDisplay,
    };
  });
}

function getTermDateWindow(termStartDate, studentTimezone) {
  const ianaTz = getIanaTz(studentTimezone);
  const monthYear = String(termStartDate || '').match(/^(\w+)\s+(\d{4})$/);
  let start = null;
  if (monthYear) {
    start = DateTime.fromFormat(`${monthYear[1]} ${monthYear[2]}`, 'MMMM yyyy', { zone: ianaTz });
    if (!start.isValid) {
      start = DateTime.fromFormat(`${monthYear[1]} ${monthYear[2]}`, 'MMM yyyy', { zone: ianaTz });
    }
  }
  if (!start || !start.isValid) {
    start = DateTime.fromISO(String(termStartDate || ''), { zone: ianaTz });
  }
  if (!start || !start.isValid) return null;

  let firstSat = start.startOf('month');
  while (firstSat.weekday !== 6) {
    firstSat = firstSat.plus({ days: 1 });
  }

  return {
    start: firstSat.startOf('day'),
    end: firstSat.plus({ weeks: 24 }).endOf('day'),
  };
}

function buildLessonPlan(match, termStartDate, studentTimezone, availabilitySlots) {
  const yr = (termStartDate || '').match(/\d{4}/);
  const year = yr ? parseInt(yr[0]) : new Date().getFullYear();
  const termWindow = getTermDateWindow(termStartDate, studentTimezone);

  let parsed = parseSlotsFromCalendlyRecords(match, studentTimezone);

  if (!parsed) {
    const exactRaw = match.exact_slots || [];
    const nearbyRaw = match.nearby_slots || [];
    const allRaw = match.all_coach_slots || [];
    const combined = [...exactRaw, ...nearbyRaw, ...allRaw];
    const seen = new Set();
    const unique = combined.filter(s => {
      const key = normalizeSlotLabel(s);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    const ianaTz = getIanaTz(studentTimezone);
    parsed = unique.map(s => {
      const dt = parseSlotDT(s, year, ianaTz);
      if (!dt) return null;
      const display = formatDTinTz(dt, ianaTz);
      const utcIso = dt.toUTC().toISO();
      return { ...display, original: s, luxonDT: dt, utc: utcIso };
    }).filter(Boolean);

    if (parsed.length === 0) return { lessons: [], typicalTime: null };
    parsed.sort((a, b) => a.date - b.date);

    const exactSet = new Set(exactRaw.map((s) => normalizeSlotLabel(s)));
    const nearbySet = new Set(nearbyRaw.map((s) => normalizeSlotLabel(s)));
    parsed.forEach(p => {
      const cleanOrig = normalizeSlotLabel(p.original);
      p.isExact = exactSet.has(cleanOrig);
      p.isNearby = nearbySet.has(cleanOrig);
    });
  }

  if (parsed.length === 0) return { lessons: [], typicalTime: null };

  // Guardrail: only keep slots that belong to the selected term window.
  // This prevents cross-year/stale slots (e.g. 2025) from appearing under a 2026 term.
  if (termWindow) {
    const zone = getIanaTz(studentTimezone);
    parsed = parsed.filter((slot) => {
      const dt = dateTimeInStudentZoneFromSlot(slot, zone);
      return dt && dt.isValid && dt >= termWindow.start && dt <= termWindow.end;
    });
  }

  if (parsed.length === 0) return { lessons: [], typicalTime: null };

  const weekGroups = {};
  for (const slot of parsed) {
    const key = getWeekMondayKeyInZone(slot, studentTimezone);
    if (!key) continue;
    if (!weekGroups[key]) weekGroups[key] = [];
    weekGroups[key].push(slot);
  }
  const sortedKeys = Object.keys(weekGroups).sort();

  const prefDayMap = buildPreferredDayRankMap(availabilitySlots);
  const zonePlan = getIanaTz(studentTimezone);
  const cardDisplayLabels = getCoachCardDisplayLabels(match, termStartDate, studentTimezone, year);
  const firstCardDisplayLabel = cardDisplayLabels[0] || null;
  // Regex-only day extraction — survives any future parseSlotDT regression so lesson 1
  // can never silently jump to a different weekday than the coach card's first line
  // (Abigail Walker / Kate ZZ used to land on Monday even though the card showed Wed).
  const firstCardWeekday = extractWeekdayFromLabel(firstCardDisplayLabel);
  const cardPatternKey = (() => {
    if (!firstCardDisplayLabel) return null;
    const l = parseSlotDT(firstCardDisplayLabel, year, zonePlan);
    if (!l || !l.isValid) return null;
    return slotPatternKey({
      dayOfWeek: l.weekday % 7,
      hours24: l.hour,
      minutes: l.minute,
    });
  })();

  /** Start from the same calendar week as the first line on the coach card (may be "all" tier only). */
  const startWeekKeyFromFirstCard = (() => {
    if (!firstCardDisplayLabel) return null;
    const l = parseSlotDT(firstCardDisplayLabel, year, zonePlan);
    if (!l || !l.isValid) return null;
    return getWeekMondayKeyInZone({ utc: l.toUTC().toISO() }, studentTimezone);
  })();
  const firstCardYmd = (() => {
    if (!firstCardDisplayLabel) return null;
    const l = parseSlotDT(firstCardDisplayLabel, year, zonePlan);
    return l && l.isValid ? l.toFormat('yyyy-LL-dd') : null;
  })();

  /**
   * Keep detail page aligned with coach card: lesson 1 should start from the
   * first line shown on the card (same pipeline as the overview, including convertSlotLabel),
   * mapped to a real Calendly slot in this plan.
   */
  function slotByCardSummaryLabel() {
    const label = firstCardDisplayLabel;
    if (!label) return null;
    const parsedFromLabel = parseSlotDT(label, year, zonePlan);
    if (parsedFromLabel && parsedFromLabel.isValid) {
      const targetMs = parsedFromLabel.startOf('minute').toMillis();
      for (const slot of parsed) {
        const dt = dateTimeInStudentZoneFromSlot(slot, zonePlan);
        if (!dt || !dt.isValid) continue;
        if (dt.startOf('minute').toMillis() === targetMs) return slot;
      }
      for (const slot of parsed) {
        const dt = dateTimeInStudentZoneFromSlot(slot, zonePlan);
        if (!dt || !dt.isValid) continue;
        if (
          dt.toFormat('yyyy-LL-dd') === parsedFromLabel.toFormat('yyyy-LL-dd')
          && dt.hour === parsedFromLabel.hour
          && dt.minute === parsedFromLabel.minute
        ) {
          return slot;
        }
      }
    }
    const clean = normalizeSlotLabel(label);
    if (clean) {
      const exactLabelMatch = parsed.find((slot) => {
        const o = normalizeSlotLabel(slot?.original || '');
        const d = normalizeSlotLabel(slot?.display || '');
        return o === clean || d === clean;
      });
      if (exactLabelMatch) return exactLabelMatch;
    }
    // Last-resort: even if parsing fully failed, at least keep lesson 1 on the same
    // weekday as the card's first line (e.g. Wed), preferring exact > nearby > earliest.
    if (firstCardWeekday) {
      const sameDow = parsed.filter((slot) => {
        const dt = dateTimeInStudentZoneFromSlot(slot, zonePlan);
        return dt && dt.isValid && dt.weekday === firstCardWeekday;
      });
      if (sameDow.length > 0) {
        const sorted = [...sameDow].sort((a, b) => {
          const aRank = a.isExact ? 0 : (a.isNearby ? 1 : 2);
          const bRank = b.isExact ? 0 : (b.isNearby ? 1 : 2);
          if (aRank !== bRank) return aRank - bRank;
          return a.date - b.date;
        });
        return sorted[0];
      }
    }
    return null;
  }

  const seededFirstSlot = slotByCardSummaryLabel();

  /** Earliest week we should start lessons: first exact/nearby on a preferred day (else any exact/nearby). Skips earlier weeks that only have unrelated `all` slots (wrong weekday / not on card). */
  function startWeekKeyFromPreferredMatches() {
    const preferredTier = parsed.filter((s) => s.isExact || s.isNearby);
    if (preferredTier.length === 0) return null;
    let pool = preferredTier;
    if (prefDayMap) {
      const onPref = preferredTier.filter((s) => preferredDaySortKey(s, prefDayMap) < 999);
      if (onPref.length > 0) pool = onPref;
    }
    let minKey = null;
    let minMs = Infinity;
    for (const slot of pool) {
      const dt = dateTimeInStudentZoneFromSlot(slot, zonePlan);
      if (!dt || !dt.isValid) continue;
      const ms = dt.toMillis();
      if (ms < minMs) {
        minMs = ms;
        minKey = getWeekMondayKeyInZone(slot, studentTimezone);
      }
    }
    return minKey;
  }

  const seededWeekKey = seededFirstSlot
    ? getWeekMondayKeyInZone(seededFirstSlot, studentTimezone)
    : null;
  // Card line #1 can be "all" tier; fallback used to look at exact|nearby only, which can start the
  // plan on a different week (e.g. Monday exact) than the first line the parent sees (e.g. Wed/Thu "all").
  const startWeekKey = seededWeekKey || startWeekKeyFromFirstCard || startWeekKeyFromPreferredMatches();
  const keysToIterate = startWeekKey
    ? sortedKeys.filter((k) => k >= startWeekKey)
    : sortedKeys;

  function sortByPriority(slots) {
    return [...slots].sort((a, b) => {
      const aRank = a.isExact ? 0 : (a.isNearby ? 1 : 2);
      const bRank = b.isExact ? 0 : (b.isNearby ? 1 : 2);
      if (aRank !== bRank) return aRank - bRank;
      const ap = preferredDaySortKey(a, prefDayMap);
      const bp = preferredDaySortKey(b, prefDayMap);
      if (ap !== bp) return ap - bp;
      return a.date - b.date;
    });
  }

  // Generate a small buffer of future weeks so "skip this week" can postpone
  // the schedule by pulling in the next real Calendly-backed slot.
  const MAX_LESSONS = 24;
  const lessons = [];
  let prevSelected = null;
  let anchorKey = null;

  for (const key of keysToIterate) {
    if (lessons.length >= MAX_LESSONS) break;
    const alternatives = sortByPriority(weekGroups[key]);
    if (alternatives.length === 0) continue;

    const firstLessonOpts = {
      forFirstLesson: lessons.length === 0,
      cardPatternKey: lessons.length === 0 ? cardPatternKey : undefined,
      firstLessonYmd: lessons.length === 0 ? firstCardYmd : undefined,
      studentIanaZone: lessons.length === 0 ? zonePlan : undefined,
    };
    // For lesson 1 only: if we know the weekday from the card label, narrow the pool
    // to that weekday so the generic fallback can't pick a different day (e.g. Monday)
    // than what the card shows (e.g. Wed for Abigail Walker / Kate ZZ).
    const pickAlternatives = (() => {
      if (lessons.length !== 0 || !firstCardWeekday) return alternatives;
      const sameDow = alternatives.filter((s) => {
        const dt = dateTimeInStudentZoneFromSlot(s, zonePlan);
        return dt && dt.isValid && dt.weekday === firstCardWeekday;
      });
      return sameDow.length > 0 ? sameDow : alternatives;
    })();
    const selected = (lessons.length === 0 && seededFirstSlot)
      ? (
        alternatives.find((alt) => canonicalUtcKey(alt?.utc) === canonicalUtcKey(seededFirstSlot?.utc))
        || alternatives.find((alt) => String(alt?.original || '').trim() === String(seededFirstSlot?.original || '').trim())
        || pickSlotForWeek(pickAlternatives, prevSelected, anchorKey, sortByPriority, firstLessonOpts)
      )
      : pickSlotForWeek(pickAlternatives, prevSelected, anchorKey, sortByPriority, firstLessonOpts);
    if (selected && !anchorKey) {
      anchorKey = slotPatternKey(selected);
    }

    lessons.push({
      weekNum: lessons.length + 1,
      mondayKey: key,
      alternatives,
      selected,
      initialSelectedDisplay: selected ? selected.display : null,
      confirmed: false, skipped: false, edited: false,
    });
    prevSelected = selected;
  }

  // Guarantee every coach gets the same baseline UX: 12 visible lesson rows
  // PLUS a buffer so "postpone this week" always has another real week to
  // slide into the active list. Coaches with sparse Calendly coverage (e.g.
  // Kate Ravenscroft had 11 weeks of consistent slots in some reschedules)
  // used to end up with only 11 lessons total, and postponing any one of
  // them left the parent with <12 visible, with no buffered weeks to pull
  // in — that's what the parent reports as "postpone week doesn't work
  // like other coaches." Project forward using the anchored weekday+time
  // pattern so the schedule is always at least 12 lessons long and there
  // are extra buffered weeks for postponing. The projected rows use the
  // coach's anchored pattern but aren't Calendly-backed (alternatives is
  // just the projection itself), so the submit path still sends a
  // well-formed slot string and the parent knows to coordinate with the
  // coach for that week if they confirm it.
  const TARGET_LESSONS = 12;
  const MIN_BUFFER_LESSONS = Math.min(MAX_LESSONS, TARGET_LESSONS + 6);
  const targetSize = Math.min(MAX_LESSONS, Math.max(MIN_BUFFER_LESSONS, TARGET_LESSONS));

  if (lessons.length < targetSize && anchorKey) {
    const anchorParts = String(anchorKey).split('-').map((n) => Number(n));
    const anchorDow = Number.isFinite(anchorParts[0]) ? anchorParts[0] : null;
    const anchorHour = Number.isFinite(anchorParts[1]) ? anchorParts[1] : null;
    const anchorMin = Number.isFinite(anchorParts[2]) ? anchorParts[2] : 0;

    // slotPatternKey stores dayOfWeek using the codebase's `weekday % 7`
    // convention: Mon=1..Sat=6, Sun=0. Convert back to Luxon's 1..7.
    const anchorLuxonWeekday = anchorDow === 0 ? 7 : anchorDow;

    if (anchorHour != null && anchorLuxonWeekday != null) {
      const lastMondayKey = lessons[lessons.length - 1]?.mondayKey;
      let cursor = lastMondayKey
        ? DateTime.fromISO(lastMondayKey, { zone: zonePlan }).plus({ weeks: 1 })
        : null;

      // Fallback: if we somehow have no last week (shouldn't happen since we
      // only pad when there's an anchorKey → at least one real lesson), use
      // the first week in the term window.
      if ((!cursor || !cursor.isValid) && termWindow) {
        cursor = termWindow.start.setZone(zonePlan).startOf('week');
      }

      const seenMondayKeys = new Set(lessons.map((l) => l.mondayKey).filter(Boolean));

      let guard = 0;
      while (
        cursor
        && cursor.isValid
        && lessons.length < targetSize
        && guard < 52 // safety: never loop past a year
      ) {
        guard += 1;
        const mondayKey = cursor.toFormat('yyyy-LL-dd');
        if (!seenMondayKeys.has(mondayKey)) {
          const daysFromMonday = (anchorLuxonWeekday - 1 + 7) % 7;
          const slotDT = cursor
            .plus({ days: daysFromMonday })
            .set({ hour: anchorHour, minute: anchorMin, second: 0, millisecond: 0 });
          if (termWindow && slotDT > termWindow.end) break;

          const fmt = formatDTinTz(slotDT, zonePlan);
          const projected = {
            ...fmt,
            original: fmt.display,
            utc: slotDT.toUTC().toISO(),
            isExact: false,
            isNearby: false,
            projected: true,
          };

          lessons.push({
            weekNum: lessons.length + 1,
            mondayKey,
            alternatives: [projected],
            selected: projected,
            initialSelectedDisplay: projected.display,
            confirmed: false, skipped: false, edited: false,
            projected: true,
          });
          seenMondayKeys.add(mondayKey);
        }
        cursor = cursor.plus({ weeks: 1 });
      }
    }
  }

  const firstSel = lessons[0]?.selected;
  const typicalTime = typicalTimeFromSelectedSlot(firstSel, studentTimezone);
  return { lessons, typicalTime };
}

function isDraftStillCompatibleWithPlan(draft, plan) {
  if (!draft || !Array.isArray(draft.lessons) || draft.lessons.length === 0) return false;
  if (!plan || !Array.isArray(plan.lessons) || plan.lessons.length === 0) return false;

  const validOriginals = new Set();
  for (const lesson of plan.lessons) {
    const alts = Array.isArray(lesson?.alternatives) ? lesson.alternatives : [];
    for (const alt of alts) {
      const key = String(alt?.original || '').trim();
      if (key) validOriginals.add(key);
    }
  }
  if (validOriginals.size === 0) return false;

  // Require every saved selected slot to still exist in the current live alternatives.
  for (const lesson of draft.lessons) {
    const selectedKey = String(lesson?.selected?.original || '').trim();
    if (!selectedKey) continue;
    if (!validOriginals.has(selectedKey)) return false;
  }
  return true;
}

/** Bump when lesson-plan / draft rules change so old localStorage drafts are discarded. */
const COACH_SELECTION_PLAN_VERSION = '2026-04-27-v15-weekly-cadence-refresh';
if (typeof window !== 'undefined' && !window.__COACH_SELECTION_VERSION_LOGGED__) {
  window.__COACH_SELECTION_VERSION_LOGGED__ = true;
  // Visible marker so we can tell from DevTools console whether this build is loaded.
  // If you still see Monday for Kate ZZ / Abigail Walker, open DevTools → Console and
  // confirm this version tag matches what's expected.
  // eslint-disable-next-line no-console
  console.info('[CoachSelection] plan version:', COACH_SELECTION_PLAN_VERSION);
}

function availabilityFingerprint(slots) {
  if (!Array.isArray(slots) || slots.length === 0) return '';
  return slots.map((s) => `${s?.day || ''}|${s?.start || ''}|${s?.end || ''}`).join(';');
}

function buildPlanSignature(match, termStartDate, studentTimezone, availabilitySlots) {
  const coach = String(match?.coach_name || '').trim().toLowerCase();
  const term = String(termStartDate || '').trim();
  const tz = String(studentTimezone || '').trim();
  const avail = availabilityFingerprint(availabilitySlots);
  const records = Array.isArray(match?.all_coach_slot_records) ? match.all_coach_slot_records : [];
  const slotSig = records
    .slice(0, 300)
    .map((r) => `${String(r?.utc || '')}|${String(r?.tier || '')}`)
    .filter(Boolean)
    .join(',');
  return `${COACH_SELECTION_PLAN_VERSION}__${coach}__${term}__${tz}__${avail}__${slotSig}`;
}

function ScheduleChangeConfirmModal({ open, onClose }) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.25rem',
      }}
      role="presentation"
      onClick={onClose}
    >
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(15, 23, 42, 0.45)' }} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-change-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 440,
          background: '#fff',
          borderRadius: 14,
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          border: '1px solid #e5e7eb',
          padding: '1.35rem 1.35rem 1.1rem',
        }}
      >
        <h2 id="schedule-change-title" style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#111827' }}>
          Time updated
        </h2>
        <p style={{ margin: '0.75rem 0 0', fontSize: '0.94rem', lineHeight: 1.55, color: '#4b5563' }}>
          You selected a different time for this lesson. To save your choice and enable the submit button, please tap{' '}
          <strong style={{ color: '#111827' }}>Click to confirm</strong> for this lesson.
        </p>
        <div style={{ marginTop: '1.1rem', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.88rem',
              fontWeight: 600,
              borderRadius: 8,
              border: 'none',
              background: '#c1009d',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

// ── LessonScheduleTable ──────────────────────────────────────────

function LessonScheduleTable({
  match,
  termStartDate,
  requestId,
  onSubmitted,
  studentTimezone,
  initialDraft,
  onDraftChange,
  availabilitySlots = [],
}) {
  const planSignature = React.useMemo(
    () => buildPlanSignature(match, termStartDate, studentTimezone, availabilitySlots),
    [match, termStartDate, studentTimezone, availabilitySlots]
  );
  const [lessons, setLessons] = useState([]);
  const [typicalTime, setTypicalTime] = useState(null);
  const [draftHydrated, setDraftHydrated] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [scheduleChangeModalOpen, setScheduleChangeModalOpen] = useState(false);
  const editRef = React.useRef(null);
  const tzLabel = studentTimezone || 'Eastern Time (ET)';
  const tzAbbr = timezoneAbbrevFromLabel(tzLabel);

  React.useEffect(() => {
    function handleClickOutside(e) {
      if (editRef.current && !editRef.current.contains(e.target)) setEditingIdx(null);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // initialDraft is read intentionally only when match/term/slots signature changes — not on every
  // parent draft save — so in-progress edits are not reset. Drafts must be in localStorage before
  // first paint (see CoachSelection useState initializer) so first hydration sees them.
  React.useEffect(() => {
    setDraftHydrated(false);
    const { lessons: plan, typicalTime: typical } = buildLessonPlan(
      match,
      termStartDate,
      studentTimezone,
      availabilitySlots
    );
    const sameSignature = Boolean(initialDraft && initialDraft.__signature && initialDraft.__signature === planSignature);
    const canReuseDraft = sameSignature && isDraftStillCompatibleWithPlan(initialDraft, { lessons: plan });

    // Diagnostic: log what the detail page actually built. This lets us see
    // from DevTools whether the lesson plan reflects the latest reschedule
    // response (Kate / Abigail had been suspected of showing pre-reschedule
    // times despite the match object carrying fresh slots).
    // eslint-disable-next-line no-console
    console.group(`[Detail] ${match?.coach_name || '?'} lesson plan build`);
    // eslint-disable-next-line no-console
    console.log('availabilitySlots →', availabilitySlots);
    // eslint-disable-next-line no-console
    console.log('match.exact_slots[0..4] →', (match?.exact_slots || []).slice(0, 5));
    // eslint-disable-next-line no-console
    console.log('match.nearby_slots[0..2] →', (match?.nearby_slots || []).slice(0, 3));
    // eslint-disable-next-line no-console
    console.log('match.all_coach_slot_records.length →', (match?.all_coach_slot_records || []).length);
    // eslint-disable-next-line no-console
    console.log('planSignature →', planSignature);
    // eslint-disable-next-line no-console
    console.log('canReuseDraft (same signature + compatible) →', canReuseDraft);
    // eslint-disable-next-line no-console
    console.log('plan lesson[0..2] →', plan.slice(0, 3).map((l) => l?.selected?.display || l?.selected?.original || '(none)'));
    // eslint-disable-next-line no-console
    console.log('typicalTime →', typical);
    // eslint-disable-next-line no-console
    console.groupEnd();

    if (canReuseDraft) {
      const rehydrated = rehydrateDraftLessons(initialDraft.lessons, plan);
      setLessons(rehydrated);
      setTypicalTime(
        typicalTimeFromSelectedSlot(rehydrated[0]?.selected, studentTimezone) || typical
      );
    } else {
      setLessons(plan);
      setTypicalTime(typical);
    }
    setDraftHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit initialDraft: avoid resetting on each parent persist
  }, [match, termStartDate, studentTimezone, planSignature]);

  React.useEffect(() => {
    if (!onDraftChange || !draftHydrated) return;
    if (!Array.isArray(lessons) || lessons.length === 0) return;
    onDraftChange({ lessons, typicalTime, __signature: planSignature });
  }, [lessons, typicalTime, onDraftChange, draftHydrated, planSignature]);

  const updateLesson = (idx, updates) => {
    setLessons(prev => prev.map((l, i) => i === idx ? { ...l, ...updates } : l));
  };

  const visibleLessonsForSubmit = lessons
    .filter((l) => !l.skipped)
    .slice(0, 12);
  const allDone = visibleLessonsForSubmit.length > 0 && visibleLessonsForSubmit.every((l) => l.confirmed);

  const handleSubmit = async () => {
    const confirmedSlots = lessons
      .filter((l) => !l.skipped)
      .slice(0, 12)
      .filter((l) => l.confirmed && l.selected)
      .map((l) => l.selected.original);
    if (confirmedSlots.length === 0) { alert('Please confirm at least one lesson.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/api/selection/${requestId}`, {
        coach_name: match.coach_name,
        selected_slots: confirmedSlots,
        selected_slot: confirmedSlots[0] || null,
        calendly_url: match.calendly_scheduling_url || null,
      });
      if (onSubmitted) onSubmitted();
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (lessons.length === 0) {
    return (
      <div style={{ padding: '1.5rem', background: '#f9fafb', borderRadius: 10, textAlign: 'center' }}>
        <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
          No available time slots found for this coach in the selected term range.
        </p>
      </div>
    );
  }

  const firstVisibleLesson = lessons.find((l) => !l.skipped && l.selected);
  const primaryFromFirstLesson = typicalTimeFromSelectedSlot(firstVisibleLesson?.selected, studentTimezone);
  const primaryLine = formatPrimaryWeeklyLine(primaryFromFirstLesson || typicalTime);
  const anchorKey = firstVisibleLesson?.selected ? slotPatternKey(firstVisibleLesson.selected) : null;
  const activeLessons = lessons
    .map((l, i) => ({ ...l, originalIdx: i }))
    .filter((l) => !l.skipped)
    .slice(0, 12);
  const skippedLessons = lessons
    .map((l, i) => ({ ...l, originalIdx: i }))
    .filter((l) => l.skipped);

  const getBoxBg = (lesson, deviatesFromWeekly) => {
    if (lesson.skipped) return '#f9fafb';
    if (lesson.edited) return '#fff5f5';
    if (lesson.confirmed) return '#f0fdf4';
    if (deviatesFromWeekly) return '#fef9c3';
    return '#fff';
  };

  return (
    <div>
      <ScheduleChangeConfirmModal
        open={scheduleChangeModalOpen}
        onClose={() => setScheduleChangeModalOpen(false)}
      />
      <p style={{ color: '#374151', fontSize: '0.9rem', marginBottom: '0.65rem', lineHeight: 1.65 }}>
        Based on your availability, your primary weekly time slot is{' '}
        <strong>{primaryLine || 'the time you selected'}</strong>.
      </p>
      <p style={{ color: '#374151', fontSize: '0.9rem', marginBottom: '0.65rem', lineHeight: 1.65 }}>
        We&apos;ve created a full schedule for your class below. You can edit or confirm each session.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {activeLessons.map((lesson, activeIdx) => {
          const idx = lesson.originalIdx;
          const isEditing = editingIdx === idx;
          const deviatesFromWeekly = !!(anchorKey && lesson.selected && slotPatternKey(lesson.selected) !== anchorKey);
          const boxBg = getBoxBg(lesson, deviatesFromWeekly);
          const boxBorder = deviatesFromWeekly && !lesson.confirmed && !lesson.edited ? '1px solid #eab308' : '1px solid #e5e7eb';
          return (
            <div key={idx} ref={isEditing ? editRef : undefined}>
              <p style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6b7280', marginBottom: '0.35rem', letterSpacing: '0.06em' }}>
                {`LESSON ${activeIdx + 1}`}
              </p>
              <div
                title={deviatesFromWeekly ? 'This time differs from your usual weekly slot' : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                  padding: '0.85rem 1rem',
                  background: boxBg,
                  borderRadius: 8,
                  border: boxBorder,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flex: '1 1 auto', minWidth: 0 }}>
                  {lesson.selected ? (
                    <>
                      <span style={{ fontWeight: 700, fontSize: '0.92rem', color: '#111827' }}>
                        {formatSelectedSlotLine(lesson.selected, studentTimezone, tzAbbr)}
                      </span>
                      <button
                        type="button"
                        onClick={() => setEditingIdx(isEditing ? null : idx)}
                        disabled={lesson.skipped}
                        style={{
                          background: 'none', border: 'none', padding: 4,
                          cursor: lesson.skipped ? 'not-allowed' : 'pointer',
                          color: lesson.skipped ? '#d1d5db' : '#6b7280',
                          flexShrink: 0,
                        }}
                        aria-label="Edit time"
                        title={lesson.skipped ? 'Skipped lesson' : 'Edit time'}
                      >
                        <Pencil size={18} />
                      </button>
                    </>
                  ) : (
                    <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>—</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (!lesson.skipped && lesson.selected) {
                      const nextConfirmed = !lesson.confirmed;
                      updateLesson(idx, { confirmed: nextConfirmed });
                    }
                  }}
                  style={{
                    background: lesson.confirmed ? '#16a34a' : '#f3f4f6',
                    border: lesson.confirmed ? 'none' : '1px solid #e5e7eb',
                    borderRadius: 999,
                    padding: '0.35rem 0.75rem',
                    cursor: (lesson.skipped || !lesson.selected) ? 'not-allowed' : 'pointer',
                    opacity: (lesson.skipped || !lesson.selected) ? 0.5 : 1,
                    color: lesson.confirmed ? '#fff' : '#6b7280',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    gap: 8,
                    flexShrink: 0,
                  }}
                  aria-label={lesson.confirmed ? 'Confirmed' : 'Confirm'}
                  disabled={lesson.skipped || !lesson.selected}
                >
                  {lesson.confirmed ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: '0.85rem' }}>
                      <Check size={16} strokeWidth={3} />
                      Confirm
                    </span>
                  ) : (
                    <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>Click to confirm</span>
                  )}
                </button>
              </div>

              {isEditing && (
                <div style={{
                  marginTop: 6, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 50, padding: '0.5rem',
                  maxHeight: 260, overflowY: 'auto',
                }}>
                  {lesson.alternatives.map((alt, ai) => {
                    const isCurrent = lesson.selected && alt.display === lesson.selected.display;
                    const isDiffDay = lesson.selected && alt.dayOfWeek !== lesson.selected.dayOfWeek;
                    const altDeviatesAnchor = !!(anchorKey && slotPatternKey(alt) !== anchorKey);
                    const slotLabel = formatSlotPickerLabel(alt, studentTimezone);
                    let pillBg = '#dbeafe', pillColor = '#1e40af';
                    if (isCurrent) { pillBg = '#bbf7d0'; pillColor = '#166534'; }
                    else if (isDiffDay) { pillBg = '#fed7aa'; pillColor = '#9a3412'; }
                    if (altDeviatesAnchor && !isCurrent) {
                      pillBg = '#fef9c3';
                      pillColor = '#a16207';
                    }
                    return (
                      <div key={ai}
                        onClick={() => {
                          if (isCurrent) {
                            setEditingIdx(null);
                            return;
                          }
                          const init = lesson.initialSelectedDisplay;
                          const isSystemSlot = init != null && String(alt.display).trim() === String(init).trim();
                          const willBecomeEdited = !isSystemSlot;
                          updateLesson(idx, {
                            selected: alt,
                            skipped: false,
                            confirmed: false,
                            edited: willBecomeEdited
                          });
                          setEditingIdx(null);
                          setScheduleChangeModalOpen(true);
                        }}
                        style={{ padding: '0.4rem 0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        {isCurrent && <span style={{ color: '#166534', fontWeight: 700, fontSize: '1rem' }}>✓</span>}
                        <span style={{
                          display: 'inline-block', fontSize: '0.82rem', fontWeight: 600,
                          color: pillColor, background: pillBg,
                          padding: '0.3rem 0.75rem', borderRadius: 999,
                          border: isCurrent ? '2px solid #22c55e' : '1px solid transparent',
                        }}>{slotLabel}</span>
                      </div>
                    );
                  })}
                  <div
                    onClick={() => {
                      // Keep selected slot so "Undo skip" can restore instantly.
                      updateLesson(idx, { skipped: true, confirmed: false, edited: false });
                      setEditingIdx(null);
                    }}
                    style={{ padding: '0.4rem 0.75rem', cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{
                      display: 'inline-block', fontSize: '0.82rem', fontWeight: 700,
                      color: '#fff', background: '#2c5282',
                      padding: '0.3rem 0.75rem', borderRadius: 999,
                    }}>POSTPONE THIS WEEK</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {skippedLessons.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <p style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6b7280', marginBottom: '0.5rem', letterSpacing: '0.06em' }}>
            POSTPONED LESSONS
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {skippedLessons.map((lesson) => (
              <div
                key={`skipped-${lesson.originalIdx}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                  padding: '0.75rem 0.9rem',
                  background: '#f9fafb',
                  borderRadius: 8,
                  border: '1px dashed #d1d5db',
                }}
              >
                <span style={{ color: '#6b7280', fontStyle: 'italic', fontSize: '0.9rem' }}>
                  {lesson.selected
                      ? `${formatSelectedSlotLine(lesson.selected, studentTimezone, tzAbbr)} (postponed)`
                    : 'Postponed week'}
                </span>
                <button
                  type="button"
                  onClick={() => updateLesson(lesson.originalIdx, { skipped: false })}
                  style={{
                    background: '#eef2ff',
                    color: '#3730a3',
                    border: '1px solid #c7d2fe',
                    borderRadius: 999,
                    padding: '0.25rem 0.7rem',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                  title="Undo postpone"
                >
                  Undo postpone
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="lesson-submit-row" style={{ marginTop: '1.25rem' }}>
        <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
          {activeLessons.filter(l => l.confirmed).length} of {activeLessons.length} lessons confirmed
          {skippedLessons.length > 0 && `, ${skippedLessons.length} postponed`}
        </span>
        <button
          onClick={handleSubmit}
          disabled={!allDone || submitting}
          style={{
            background: allDone && !submitting ? BRAND : '#d1d5db',
            color: '#fff', border: 'none', borderRadius: 8,
            padding: '0.85rem 2.5rem', fontSize: '1rem', fontWeight: 700,
            cursor: allDone && !submitting ? 'pointer' : 'not-allowed',
          }}
        >
          {submitting ? 'Saving...' : 'SUBMIT'}
        </button>
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────

function CoachSelection() {
  const { requestId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCoachName, setSelectedCoachName] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [declineComplete, setDeclineComplete] = useState(false);
  const [scheduleDrafts, setScheduleDrafts] = useState(() => loadDraftsFromStorage(requestId));
  const [rescheduleSlots, setRescheduleSlots] = useState([]);
  const [rescheduling, setRescheduling] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [selectedRescheduleDay, setSelectedRescheduleDay] = useState(null);
  // Bumped every time a reschedule succeeds. We use this as part of the
  // LessonScheduleTable `key` so the detail page is force-remounted with
  // a fresh internal state whenever the parent's availability changes,
  // making it impossible for any previously-built plan, draft, or cached
  // lesson selection to bleed through into the new schedule view.
  const [scheduleVersion, setScheduleVersion] = useState(0);
  const draftMigrationDoneRef = React.useRef({});

  const hasAvailabilityChanges = React.useMemo(() => {
    const original = Array.isArray(data?.availability_slots) ? data.availability_slots : [];
    const current = Array.isArray(rescheduleSlots) ? rescheduleSlots : [];
    if (original.length !== current.length) return true;
    const key = (s) => `${s.day}__${s.start}__${s.end}`;
    const originalKeys = new Set(original.map(key));
    for (const s of current) {
      if (!originalKeys.has(key(s))) return true;
    }
    return false;
  }, [data?.availability_slots, rescheduleSlots]);

  useEffect(() => { fetchSelectionData(); }, [requestId]); // eslint-disable-line

  useEffect(() => {
    setRescheduleSlots(Array.isArray(data?.availability_slots) ? data.availability_slots : []);
  }, [data?.availability_slots]);

  useEffect(() => {
    if (!requestId) {
      setScheduleDrafts({});
      return;
    }
    setScheduleDrafts(loadDraftsFromStorage(requestId));
  }, [requestId]);

  useEffect(() => {
    if (!requestId) return;
    try {
      window.localStorage.setItem(
        `coachSelectionDrafts:${requestId}`,
        JSON.stringify(scheduleDrafts || {})
      );
    } catch {
      // ignore storage errors
    }
  }, [scheduleDrafts, requestId]);

  // One-time migration per request: remove stale/incompatible drafts so
  // existing users stop carrying old schedules after matching updates.
  useEffect(() => {
    if (!requestId || !data || !Array.isArray(data.matches)) return;
    if (draftMigrationDoneRef.current[requestId]) return;

    const currentDrafts = scheduleDrafts && typeof scheduleDrafts === 'object' ? scheduleDrafts : {};
    const nextDrafts = {};
    let changed = false;

    const matchByCoach = new Map(
      data.matches.map((m) => [String(m?.coach_name || '').trim(), m]).filter(([k]) => !!k)
    );

    for (const [coachName, draft] of Object.entries(currentDrafts)) {
      const match = matchByCoach.get(String(coachName || '').trim());
      if (!match) {
        changed = true;
        continue;
      }
      const { lessons: plan } = buildLessonPlan(
        match,
        data?.term_start_date,
        data?.timezone || 'Eastern Time (ET)',
        data?.availability_slots || []
      );
      const expectedSignature = buildPlanSignature(
        match,
        data?.term_start_date,
        data?.timezone || 'Eastern Time (ET)',
        data?.availability_slots || []
      );
      const signatureOk = Boolean(draft && draft.__signature && draft.__signature === expectedSignature);
      const compatible = signatureOk && isDraftStillCompatibleWithPlan(draft, { lessons: plan });
      if (compatible) {
        nextDrafts[coachName] = draft;
      } else {
        changed = true;
      }
    }

    draftMigrationDoneRef.current[requestId] = true;
    if (changed) {
      setScheduleDrafts(nextDrafts);
      try {
        window.localStorage.setItem(`coachSelectionDrafts:${requestId}`, JSON.stringify(nextDrafts));
      } catch {
        // ignore storage errors
      }
    }
  }, [requestId, data, scheduleDrafts]);

  const fetchSelectionData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/selection/${requestId}`);
      if (res.data.success === false && res.data.needs_rematch) {
        setData({ ...res.data, matches: [] });
      } else {
        setData(res.data);
      }
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRunMatching = async () => {
    try {
      setLoading(true);
      await axios.post(`${API_BASE}/api/match/${requestId}`);
      await fetchSelectionData();
    } catch (err) {
      setError('Matching failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const matches = [...(data?.matches || [])].sort((a, b) => (b.score_pct || 0) - (a.score_pct || 0));
  const studentTimezone = data?.timezone || 'Eastern Time (ET)';
  const termYear = (() => {
    const m = (data?.term_start_date || '').match(/\d{4}/);
    return m ? parseInt(m[0]) : new Date().getFullYear();
  })();

  function buildTimeSummary(match) {
    return getCoachCardDisplayLabels(match, data?.term_start_date || '', studentTimezone, termYear);
  }

  function scoreBadgeColor(score) {
    const s = Number(score) || 0;
    if (s >= 90) return { bg: '#bbf7d0', text: '#14532d' }; // dark green (green-200 / green-900)
    if (s >= 75) return { bg: '#dcfce7', text: '#166534' }; // medium green (green-100 / green-800)
    if (s >= 60) return { bg: '#ecfccb', text: '#3f6212' }; // light green (lime-100 / lime-800)
    if (s >= 45) return { bg: '#ffedd5', text: '#c2410c' }; // orange (orange-100 / orange-700)
    if (s >= 30) return { bg: '#fef9c3', text: '#a16207' }; // yellow (yellow-100 / yellow-700)
    if (s >= 15) return { bg: '#ffe4e6', text: '#be123c' }; // pink-red (rose-100 / rose-700)
    return { bg: '#f3f4f6', text: '#6b7280' }; // gray
  }

  const handleReschedule = async () => {
    if (!requestId) return;
    if (rescheduleSlots.length < 3) {
      alert('Please select at least 3 one-hour slots.');
      return;
    }
    setRescheduling(true);
    try {
      const res = await axios.post(`${API_BASE}/api/intake/${requestId}/reschedule`, {
        availability_slots: rescheduleSlots
      });

      // Availability changed → any per-coach lesson-plan drafts stored in
      // localStorage are stale (they were built against the OLD availability
      // window and the OLD categorized Calendly slots). Wipe them so the
      // detail page never re-hydrates an obsolete schedule after the user
      // clicks a coach card. Also reset the migration sentinel so the next
      // pass through the migration effect can run again against fresh data.
      setScheduleDrafts({});
      try {
        window.localStorage.removeItem(`coachSelectionDrafts:${requestId}`);
      } catch {
        // ignore storage errors
      }
      draftMigrationDoneRef.current[requestId] = false;
      // Force-remount the detail view on next open so no previously-built
      // lesson plan, selected slot, or internal hydration state can survive
      // a reschedule.
      setScheduleVersion((v) => v + 1);
      setSelectedCoachName(null);
      setRescheduleOpen(false);
      setError(null);

      // Use the reschedule response directly as the SINGLE source of truth.
      // The backend now returns fully-enriched matches (same shape as GET
      // /api/selection — including coach_detail bio/video AND all_coach_slot_records
      // with per-slot tier markers). Previously we did a second GET fetch
      // here, but that meant TWO independent Calendly API calls for each
      // coach per reschedule. For coaches using duration-fallback event
      // type selection (Abigail Walker) or multi-URL accounts, those two
      // calls could surface different slot lists → the card would show
      // Thu/Fri "exact" times while the detail page's refetch landed on a
      // set that mis-ranked them, leaving Lesson 1 on Mon/Wed. With one
      // call, the card and the detail plan are built from identical records.
      const matchesFromReschedule = Array.isArray(res.data?.matches) ? res.data.matches : [];

      // Diagnostic: surface the first exact slot returned for every coach so
      // the user (and us) can tell from DevTools whether the reschedule
      // response actually contains fresh data for Kate / Abigail or if the
      // staleness is coming from elsewhere.
      // eslint-disable-next-line no-console
      console.group('[Reschedule] response summary');
      // eslint-disable-next-line no-console
      console.log('availability sent →', rescheduleSlots);
      // eslint-disable-next-line no-console
      console.log('availability echoed by server →', res.data?.availability_slots);
      matchesFromReschedule.forEach((m) => {
        // eslint-disable-next-line no-console
        console.log(
          `${m.coach_name}: exact[0]=${(m.exact_slots || [])[0] || '—'} | nearby[0]=${(m.nearby_slots || [])[0] || '—'} | records=${(m.all_coach_slot_records || []).length}`
        );
      });
      // eslint-disable-next-line no-console
      console.groupEnd();

      setData((prev) => ({
        ...(prev || {}),
        success: res.data?.success !== false,
        student_name: res.data?.student_name || prev?.student_name,
        program: res.data?.program || prev?.program,
        timezone: res.data?.timezone || prev?.timezone,
        term_start_date: res.data?.term_start_date || prev?.term_start_date,
        availability_slots: res.data?.availability_slots || rescheduleSlots,
        status: res.data?.status || prev?.status,
        matches: matchesFromReschedule
      }));

      if (res.data?.warning) {
        alert(`Rescheduled, but matching warning: ${res.data.warning}`);
      }
    } catch (err) {
      alert('Failed to reschedule: ' + (err.response?.data?.error || err.message));
    } finally {
      setRescheduling(false);
    }
  };

  const AvailabilityPanel = ({ compact = false }) => (
    <div style={{ marginBottom: compact ? '1rem' : '1.4rem', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1rem 1rem 0.9rem' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '0.75rem', color: '#374151', fontSize: '0.88rem' }}>
        <span><strong>Class:</strong> {data?.program || '-'}</span>
        <span><strong>Term:</strong> {data?.term_start_date || '-'}</span>
      </div>

      <div style={{ marginBottom: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
        {rescheduleSlots.length > 0 ? rescheduleSlots.map((slot, i) => (
          <span key={`${slot.day}-${slot.start}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f3e8ff', color: '#6b21a8', padding: '4px 8px', borderRadius: 999, fontSize: '0.74rem', fontWeight: 600 }}>
            <Clock size={12} />
            {slot.day} {formatHourLabel(slot.start)}–{formatHourLabel(slot.end)}
          </span>
        )) : <span style={{ color: '#9ca3af', fontSize: '0.82rem' }}>No availability selected</span>}
      </div>
      {Array.isArray(matches) && matches.length < 3 && (
        <p style={{ margin: '-0.15rem 0 0.7rem', fontSize: '0.8rem', fontStyle: 'italic', color: '#6b7280' }}>
          Not seeing enough options? Try adding more slots to your availability.
        </p>
      )}

      {rescheduleOpen && (
        <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '0.75rem', marginTop: '0.25rem' }}>
          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
            {DAYS.map(day => {
              const count = rescheduleSlots.filter(s => s.day === day).length;
              const active = selectedRescheduleDay === day;
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => setSelectedRescheduleDay(active ? null : day)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 8,
                    border: '1px solid #e5e7eb',
                    background: active ? BRAND : (count > 0 ? '#f5f3ff' : '#fff'),
                    color: active ? '#fff' : (count > 0 ? '#6d28d9' : '#374151'),
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  {day.slice(0, 3)} {count > 0 ? `(${count})` : ''}
                </button>
              );
            })}
          </div>

          {selectedRescheduleDay && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.35rem', marginBottom: '0.6rem' }}>
              {TIME_SLOTS.map((ts) => {
                const idx = rescheduleSlots.findIndex(s => s.day === selectedRescheduleDay && s.start === ts.start);
                const selected = idx !== -1;
                return (
                  <button
                    key={`${selectedRescheduleDay}-${ts.start}`}
                    type="button"
                    onClick={() => {
                      if (selected) {
                        setRescheduleSlots(prev => prev.filter((_, i) => i !== idx));
                      } else {
                        setRescheduleSlots(prev => [...prev, { day: selectedRescheduleDay, start: ts.start, end: ts.end }]);
                      }
                    }}
                    style={{
                      padding: '7px 8px',
                      borderRadius: 8,
                      border: `1px solid ${selected ? BRAND : '#d1d5db'}`,
                      background: selected ? BRAND : '#fff',
                      color: selected ? '#fff' : '#374151',
                      fontSize: '0.74rem',
                      cursor: 'pointer'
                    }}
                  >
                    {ts.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', paddingBottom: '0.15rem' }}>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setRescheduleOpen(v => !v)}>
          {rescheduleOpen ? 'Hide availability editor' : 'Edit availability'}
        </button>
        {(() => {
          const disabled = rescheduling || rescheduleSlots.length < 3 || !hasAvailabilityChanges;
          return (
            <button
              type="button"
              onClick={handleReschedule}
              disabled={disabled}
              aria-disabled={disabled}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '0.4rem 1rem',
                borderRadius: 999,
                border: 'none',
                fontSize: '0.85rem',
                fontWeight: 600,
                background: disabled ? '#d1d5db' : BRAND,
                color: '#fff',
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              {rescheduling && (
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: '2px solid rgba(255,255,255,0.5)',
                    borderTopColor: '#fff',
                    animation: 'spin 0.8s linear infinite',
                  }}
                />
              )}
              <span>{rescheduling ? 'Rescheduling…' : 'Reschedule'}</span>
            </button>
          );
        })()}
      </div>
    </div>
  );

  if (declineComplete) {
    return <DeclineMatchThankYouScreen />;
  }

  // ── Loading ──
  if (loading) {
    return (
      <div className="submitting-screen match-results-page">
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        <div style={{
          width: 40, height: 40, border: '3px solid #e5e7eb', borderTopColor: BRAND,
          borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1.5rem',
        }} />
        <p style={{ color: '#6b7280' }}>Loading coach options...</p>
      </div>
    );
  }

  // ── Error ──
  if (error && !data?.needs_rematch) {
    return (
      <div className="match-results-page">
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
          padding: '0.75rem 1rem', marginBottom: '1.5rem', color: '#991b1b', fontSize: '0.875rem',
        }}>
          {error}
        </div>
        <button onClick={() => navigate('/')} style={{
          background: 'none', border: 'none', color: BRAND, cursor: 'pointer',
          fontSize: '0.85rem', fontWeight: 600, padding: 0,
        }}>
          ← Back to form
        </button>
      </div>
    );
  }

  // ── Needs rematch ──
  if (data?.needs_rematch) {
    return (
      <div className="match-results-page">
        <button onClick={() => navigate('/')} style={{
          background: 'none', border: 'none', color: BRAND, cursor: 'pointer',
          fontSize: '0.85rem', fontWeight: 600, padding: 0, marginBottom: '1.5rem',
        }}>
          ← Back to form
        </button>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: BRAND, marginBottom: '0.5rem' }}>
          {data.student_name}
        </h1>
        <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
          No coach options available yet. Run matching to get recommendations.
        </p>
        <button onClick={handleRunMatching} disabled={loading} style={{
          background: BRAND, color: '#fff', border: 'none', borderRadius: 8,
          padding: '0.75rem 1.5rem', fontSize: '1rem', fontWeight: 600,
          cursor: loading ? 'wait' : 'pointer',
        }}>
          {loading ? 'Running matching...' : 'Run matching'}
        </button>
      </div>
    );
  }

  // ── Completion screen ──
  if (submitted) {
    return (
      <div className="completion-screen match-results-page">
        <div style={{
          textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.25rem',
        }}>
          <img src="/logo-letterly.png" alt="Letterly" style={{ height: 56, marginBottom: '0.25rem' }} />
          <p style={{ fontWeight: 700, color: '#166534', fontSize: '1.5rem', margin: 0 }}>
            Completed
          </p>
          <p style={{ color: '#6b7280', fontSize: '0.95rem', margin: 0, maxWidth: 360 }}>
            Your coach has been selected. You will receive a booking confirmation shortly.
          </p>
        </div>
      </div>
    );
  }

  // ── Detail view for a selected coach ──
  if (selectedCoachName !== null) {
    const match = matches.find(m => m.coach_name === selectedCoachName);
    if (!match) { setSelectedCoachName(null); return null; }
    const matchIndex = matches.indexOf(match);
    const optionLabel = ['A', 'B', 'C'][matchIndex] ?? (matchIndex + 1);

    const bio = (match.coach_detail?.bio || '').trim() || 'No bio provided.';
    const videoUrl = toEmbedUrl(match.coach_detail?.url || '');
    const hasVideo = !!videoUrl;

    return (
      <div className="match-results-page">
        <button
          onClick={() => setSelectedCoachName(null)}
          style={{
            background: 'none', border: 'none', color: BRAND, cursor: 'pointer',
            fontSize: '0.85rem', fontWeight: 600, padding: 0, marginBottom: '1.5rem',
          }}
        >
          Back to all options
        </button>

        <p style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: 500 }}>Option {optionLabel}</p>
        <h1 className="detail-heading" style={{ fontWeight: 700, color: BRAND, marginBottom: '1.5rem' }}>
          {match.coach_name}
        </h1>

        <div style={{
          borderRadius: 12, overflow: 'hidden', marginBottom: '2rem',
          background: hasVideo ? '#000' : '#f3f4f6',
          position: 'relative',
          ...(hasVideo ? { paddingBottom: '56.25%' } : { padding: '3rem', minHeight: 200 }),
        }}>
          {hasVideo ? (
            <iframe
              src={videoUrl}
              title="Coach intro video"
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af', fontSize: '0.9rem' }}>
              No intro video available
            </div>
          )}
        </div>

        <h3 style={{ fontWeight: 700, marginBottom: '0.5rem' }}>Coach Bio</h3>
        <p style={{ color: '#374151', lineHeight: 1.75, marginBottom: '2.5rem', fontSize: '0.95rem' }}>
          {bio}
        </p>

        <LessonScheduleTable
          // Force a full remount when the coach OR the underlying availability/slots
          // change. Without this key, React reuses the same component instance across
          // a reschedule — which means the internal `lessons` / `typicalTime` state
          // from the previous availability can survive (the signature-guarded effect
          // does reset it, but any subtle race or stale useMemo had been letting the
          // detail page keep showing pre-reschedule times). The version bump in
          // handleReschedule guarantees this key changes on every reschedule.
          key={`${match.coach_name}__${scheduleVersion}__${availabilityFingerprint(data?.availability_slots || [])}`}
          match={match}
          termStartDate={data?.term_start_date}
          requestId={requestId}
          studentTimezone={studentTimezone}
          availabilitySlots={data?.availability_slots || []}
          initialDraft={scheduleDrafts[match.coach_name] || null}
          onDraftChange={(draft) => {
            setScheduleDrafts((prev) => ({ ...prev, [match.coach_name]: draft }));
          }}
          onSubmitted={() => {
            setSubmitted(true);
            confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 } });
            setTimeout(() => {
              confetti({ particleCount: 80, angle: 60, spread: 55, origin: { x: 0 } });
              confetti({ particleCount: 80, angle: 120, spread: 55, origin: { x: 1 } });
            }, 200);
          }}
        />
      </div>
    );
  }

  // ── Overview: coach suggestion cards ──
  return (
    <div className="match-results-page">
      <p style={{ fontSize: '0.9rem', color: '#374151', fontWeight: 500 }}>
        Coach Profile Suggestions
      </p>
      <h1 className="match-overview-heading" style={{ fontWeight: 700, color: BRAND, marginBottom: '0.75rem' }}>
        {data?.student_name}
      </h1>
      <p style={{ color: '#6b7280', lineHeight: 1.7, marginBottom: '2rem', maxWidth: 600 }}>
        {Array.isArray(matches) && matches.length < 3
          ? 'Here are a few coach options for you to choose from, based on your preferences and schedule needs.'
          : 'Here are three coach options for you to choose from, based on your preferences and schedule needs.'}
        Each profile includes a short bio, an intro video, and a brief explanation of why it may be a good fit.
        Please review the options and select the coach you would like to move forward with.
      </p>

      <AvailabilityPanel />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        {matches.map((match, i) => {
          const displayTimes = buildTimeSummary(match);
          const badge = scoreBadgeColor(match.score_pct);

          // Key by coach identity + scheduleVersion. Index-only keys let React
          // reuse the same card DOM across a reschedule: the "top coach" slot
          // (i=0) kept its previous coach's painted text until the next paint,
          // so the parent saw the pre-reschedule time summary on the card they
          // were already looking at (most commonly the prior top coach since
          // that card sits right above the reschedule button). Including
          // scheduleVersion force-refreshes every card whenever availability
          // changes, regardless of rank stability.
          return (
            <div
              key={`${match.coach_name || i}__${scheduleVersion}`}
              onClick={() => setSelectedCoachName(match.coach_name)}
              style={{
              background: '#fff', borderRadius: 12,
              border: `2px solid ${i === 0 ? '#bfdbfe' : '#e5e7eb'}`,
              padding: '1.25rem 1.5rem', cursor: 'pointer', transition: 'all 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#93c5fd'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = i === 0 ? '#bfdbfe' : '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div className="coach-card-row">
                <div>
                  <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1f2937', marginBottom: '0.3rem' }}>
                    {match.coach_name}
                  </h2>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    {displayTimes.length > 0
                      ? displayTimes.join(' or ')
                      : 'Schedule upon selection'}
                  </div>
                </div>
                <span style={{
                  background: badge.bg, color: badge.text, borderRadius: 20,
                  padding: '0.35rem 1rem', fontWeight: 700, fontSize: '0.85rem',
                  border: `1.5px solid ${badge.text}22`, whiteSpace: 'nowrap',
                }}>
                  {match.score_pct}% Match
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <DeclineCoachMatches requestId={requestId} onComplete={() => setDeclineComplete(true)} />
    </div>
  );
}

export default CoachSelection;
